import "./polyfills";

import * as Sentry from "@sentry/react";
import { createRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { setDefaultOptions } from "date-fns";
import { zhCN } from "date-fns/locale";
import ErrorPrint from "./features/shared/components/error-print";
import { isTailwindV4SupportedBrowser } from "./lib/utils/browser-support";
import NotFound from "./features/shared/components/not-found";
import { createQueryClient } from "./lib/utils/query-client";
import { routeTree } from "./routeTree.gen";
import { PublicEnvSchema } from "./lib/env";
import {
  scrubSentryBreadcrumb,
  scrubSentryEvent,
  sentryTracePropagationTargets,
  sentryTracesSampler,
} from "./observability/sentry-config";

setDefaultOptions({ locale: zhCN });

export function getRouter() {
  const queryClient = createQueryClient();
  const router = createRouter({
    routeTree,
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    context: {
      queryClient,
      authed: undefined!,
      user: undefined!,
      roles: undefined!,
      theme: undefined!,
      env: undefined!,
    },
    defaultPendingMs: 300,
    defaultPendingMinMs: 100,
    defaultErrorComponent: ErrorPrint,
    defaultNotFoundComponent: NotFound,
  });

  setupRouterSsrQueryIntegration({ router, queryClient });

  if (!router.isServer) {
    if (!isTailwindV4SupportedBrowser() && window.location.pathname !== "/browser-upgrade") {
      const redirect = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const nextLocation = `/browser-upgrade?redirect=${encodeURIComponent(redirect)}`;
      window.location.replace(nextLocation);
      return router;
    }

    Sentry.init({
      dsn: window.__INJECTED_ENV__.SENTRY_DSN,
      environment: window.__INJECTED_ENV__.SENTRY_ENVIRONMENT,
      release: window.__INJECTED_ENV__.SENTRY_RELEASE,
      sendDefaultPii: false,
      integrations: [
        // TanStack Router 追踪集成
        Sentry.tanstackRouterBrowserTracingIntegration(router),
        // 浏览器追踪集成
        Sentry.browserTracingIntegration(),
        // 浏览器性能分析
        Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration({
          blockAllMedia: true,
          maskAllText: true,
        }),
      ],
      enableLogs: true,
      tracePropagationTargets: sentryTracePropagationTargets,
      tracesSampler: sentryTracesSampler,
      replaysSessionSampleRate: 0.01,
      replaysOnErrorSampleRate: 1.0,
      profileSessionSampleRate: 0.05,
      profileLifecycle: "trace",
      attachStacktrace: true,
      enabled: !import.meta.env.DEV && Boolean(window.__INJECTED_ENV__.SENTRY_DSN),
      beforeSend: (event) => scrubSentryEvent(event),
      beforeSendTransaction: (event) => scrubSentryEvent(event),
      beforeBreadcrumb: (breadcrumb) => scrubSentryBreadcrumb(breadcrumb),
    });
  }

  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}

declare global {
  interface Window {
    __INJECTED_ENV__: PublicEnvSchema;
  }
}
